import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { HiArrowSmLeft } from "react-icons/hi";
import { FiAlertCircle } from "react-icons/fi";
import CategoryIcon from "@mui/icons-material/Category";
import PetsIcon from "@mui/icons-material/Pets";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import HotelIcon from "@mui/icons-material/Hotel";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
// import StorefrontIcon from "@mui/icons-material/Storefront";
import { httpService } from "../../services/httpService";
// Import the service drawer components
import NewServiceDrawer from "./NewServiceDrawer";
import BoardingDrawer from "./BoardingDrawer";
import DaycareDrawer from "./DaycareDrawer";
import DogWalkingDrawer from "./DogWalkingDrawer";
import VetClinicDrawer from "./VetClinicDrawer";
// import PetStoreDrawer from "./PetStoreDrawer";
import { useProfile } from "../../context/ProfileContext";
import { TokenContext } from "../../context/TokenContext";

/**
 * PetServiceCategoryDrawer
 * -----------------------
 * A comprehensive drawer that shows service categories and renders the appropriate
 * service form within the same drawer based on the selected category.
 */
const PetServiceCategoryDrawer = ({
  isOpen = false,
  onClose = () => {},
  onUpdate = () => {},
  clientId = null,
  itemData = {},
  orderId = null,
  fromCalendar = false,
  selectedSlotInfo = null,
  editMode = false,
  onRefreshData = null,
  appointmentStatusRef = null,
  autoOpenPetForm = false,
  onBackToEnquiry = null,
  saleType = null,
  clientName = null,
  clientNumber = null,
  isServiceEditMode = false,
  isPetDetails = false,
  drawerWidth = "lg:w-1/2",
  existingAppointments = [],
  checkEmployeeDoubleBooking = null,
  setSaveEnquiryClicked = null,
}) => {
  const { rawServiceCategory, roleMenuPermissions, isActionEnabled } = useProfile();
  const { decodedData } = useContext(TokenContext);
  const { data } = decodedData;
  
  // Get employee assigned service categories from token
  const employeeServiceCategories = data?.vendor?.employeeServiceCategory || [];
  const isEmployee = data?.user?.role_data?.name === "executive";
  
  // Create a Set of assigned service category IDs for fast lookup
  const assignedCategoryIds = useMemo(() => {
    return new Set(
      employeeServiceCategories.map(item => 
        item.service_category_id || item.service_category_type_id
      )
    );
  }, [employeeServiceCategories]);

  const hasListingBookingsActions = useMemo(() => {
    const listingMenu = roleMenuPermissions.find(
      (menu) => menu.menu_name?.trim() === "Listing Bookings",
    );
    return listingMenu?.actions?.length > 0;
  }, [roleMenuPermissions]);

  const categories = useMemo(() => {
    if (!rawServiceCategory) return [];

    const isActiveFlag = (st) =>
      st &&
      (st.is_active === 1 || st.is_active === "1" || st.is_active === true);

    // Helper to map a category to a tab action (if any)
    const getActionForCategory = (cat) => {
      if (cat.type_id === 2) return "Boarding tab";
      if (cat.type_id === 1) {
        const catName = (cat.service_category_name || "").toLowerCase();
        if (catName.includes("vet") || catName.includes("clinic")) {
          return "Vet tab";
        }
        return "Grooming tab";
      }
      return null;
    };

    // 1️⃣ Filter by saleType (existing logic)
    let filtered = rawServiceCategory.filter((item) => {
      if (saleType === "vet") {
        const catName = (item.service_category_name || "").toLowerCase();
        return (
          catName.includes("vet") ||
          catName.includes("clinic") ||
          item.service_type?.some((st) => {
            const stName = (st.service_type_name || "").toLowerCase();
            return stName.includes("vet") || stName.includes("clinic");
          })
        );
      }
      if (saleType === "grooming") {
        const catName = (item.service_category_name || "").toLowerCase();
        return (
          catName.includes("groom") ||
          item.service_type?.some((st) => {
            const stName = (st.service_type_name || "").toLowerCase();
            return stName.includes("groom");
          })
        );
      }
      if (saleType === "appointment") return item.type_id === 1;
      if (saleType === "booking") return item.type_id === 2;
      return true;
    });

    // 🚨 NEW: Filter by employee assigned service categories
    if (isEmployee && assignedCategoryIds.size > 0) {
      filtered = filtered.filter((cat) => {
        // Check if this category or any of its service types are assigned to the employee
        const categoryId = cat.service_category_id || cat._id;
        
        // If the category itself is assigned
        if (assignedCategoryIds.has(categoryId)) {
          return true;
        }
        
        // Check if any service type within this category is assigned
        if (Array.isArray(cat.service_type)) {
          return cat.service_type.some((st) => {
            const stId = st.service_category_type_id || st._id;
            return assignedCategoryIds.has(stId);
          });
        }
        
        return false;
      });
    }

    // 2️⃣ Build final category list
    const hasServiceTypes = filtered.some(
      (cat) =>
        Array.isArray(cat.service_type) && cat.service_type.length > 0,
    );

    let result = [];

    if (hasServiceTypes) {
      // Existing logic: flatten service types from each category
      result = filtered
        .map((cat) => ({
          ...cat,
          service_type: Array.isArray(cat.service_type)
            ? cat.service_type
                .filter(isActiveFlag)
                .filter((st) => {
                  if (st.service_type_name === "Pet Resort") return false;
                  if (st.service_type_name === "Day Care Services") return false;
                  return true;
                })
                // 🚨 Filter service types by employee assignment
                .filter((st) => {
                  if (isEmployee && assignedCategoryIds.size > 0) {
                    const stId = st.service_category_type_id || st._id;
                    return assignedCategoryIds.has(stId);
                  }
                  return true;
                })
                .filter((st) => {
                  if (hasListingBookingsActions) {
                    const action = getActionForCategory(cat);
                    if (action === "petstore") return true;
                    if (!action) return false;
                    return isActionEnabled("Listing Bookings", action);
                  }
                  return true;
                })
            : [],
        }))
        .filter((cat) => cat.service_type.length > 0)
        .filter((cat) => cat.service_category_name !== "Day Care");
    } else {
      // Fallback: no service_type – treat each category as a selectable item
      result = filtered
        .filter((cat) => cat.isActive !== false && cat.type_id != 3)
        .map((cat) => ({
          ...cat,
          service_type: [
            {
              service_category_type_id: cat.service_category_id,
              service_type_name: cat.service_category_name,
              service_type_id: cat.type_id,
              is_active: cat.isActive ? 1 : 0,
            },
          ],
        }))
        .filter((cat) => {
          // 🚨 Filter by employee assignment for fallback categories
          if (isEmployee && assignedCategoryIds.size > 0) {
            const categoryId = cat.service_category_id || cat._id;
            return assignedCategoryIds.has(categoryId);
          }
          return true;
        })
        .filter((cat) => {
          if (hasListingBookingsActions) {
            const action = getActionForCategory(cat);
            if (action === "petstore") return true;
            if (!action) return false;
            return isActionEnabled("Listing Bookings", action);
          }
          return true;
        })
        .filter((cat) => cat.service_category_name !== "Day Care");
    }

    return result;
  }, [
    rawServiceCategory,
    saleType,
    hasListingBookingsActions,
    isActionEnabled,
    isEmployee,
    assignedCategoryIds,
  ]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryData, setSelectedCategoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formStatus, setFormStatus] = useState({
    loading: false,
    total: 0,
    pending: 0,
    pendingMandatory: 0,
    completed: 0,
    names: [],
  });
  // const [petTypes, setPetTypes] = useState([]);
  // Add state to track if we're in "Add New Pet" mode
  const [isInPetForm, setIsInPetForm] = useState(false);
  // Add ref to communicate with service drawer
  const serviceDrawerRef = useRef(null);

  const normalizeFormId = (value = "") => String(value || "").trim();

  const getItemFormIds = useCallback(() => {
    const rawIds = Array.isArray(itemData?.form_ids) ? itemData.form_ids : [];
    return rawIds
      .map((formId) => {
        if (typeof formId === "object") {
          return normalizeFormId(formId._id || formId.form_id || formId.id || "");
        }
        return normalizeFormId(formId);
      })
      .filter(Boolean);
  }, [itemData?.form_ids]);

  const isRelevantOrderForm = useCallback(
    (form) => {
      const formId = normalizeFormId(form.form_id || form._id || form.id);
      const itemFormIds = getItemFormIds();
      const itemRefId = normalizeFormId(itemData?.ref_id || itemData?.id || itemData?._id);
      const itemId = normalizeFormId(itemData?._id || itemData?.item_id);
      const petId = normalizeFormId(itemData?.vendor_pet_id || itemData?.pet_id);
      const itemType = String(itemData?.item_type || itemData?.type || "").toLowerCase();
      const formItemRefId = normalizeFormId(form.item_ref_id);
      const formItemId = normalizeFormId(form.item_id);
      const formPetId = normalizeFormId(form.pet_id);
      const formItemType = String(form.item_type || "").toLowerCase();

      if (formId && itemFormIds.length > 0 && itemFormIds.includes(formId)) {
        return true;
      }
      if (itemRefId && (formItemRefId === itemRefId || formItemId === itemRefId)) {
        return true;
      }
      if (itemId && (formItemId === itemId || formItemRefId === itemId)) {
        return true;
      }
      if (petId && formPetId && formPetId === petId) {
        if (!formItemType || !itemType || formItemType === itemType) {
          return true;
        }
      }
      if (itemType && formItemType && itemType === formItemType) {
        return true;
      }
      if (!formItemRefId && !formItemId && !formPetId && itemFormIds.length === 0) {
        return true;
      }
      return false;
    },
    [getItemFormIds, itemData],
  );

  const fetchOrderFormStatus = useCallback(async () => {
    const itemFormIds = getItemFormIds();
    if (!orderId && itemFormIds.length === 0) {
      setFormStatus({
        loading: false,
        total: 0,
        pending: 0,
        pendingMandatory: 0,
        completed: 0,
        names: [],
        message: "",
      });
      return;
    }

    setFormStatus((prev) => ({ ...prev, loading: true }));

    try {
      let allForms = [];
      if (orderId) {
        const res = await httpService.get(`/vendors/getOrderFormsStatus/${String(orderId)}`, {
          skipToast: true,
        });
        const rawData = res?.data?.data || res?.data || res || {};
        allForms = Array.isArray(rawData?.forms)
          ? rawData.forms
          : Array.isArray(rawData)
          ? rawData
          : Array.isArray(res?.forms)
          ? res.forms
          : [];
      } else {
        const res = await httpService.get("/vendors/getAllForms", {
          skipToast: true,
        });
        allForms = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
      }

      const relevantForms = allForms.filter((form) => isRelevantOrderForm(form));
      const finalForms = relevantForms.map((form) => ({
        ...form,
        completed:
          !!form.completed ||
          String(form.status || "").toLowerCase() === "completed" ||
          !!form.answer_id ||
          !!form.form_response_id,
      }));

      const totalCount = finalForms.length;
      const completedCount = finalForms.filter((form) => form.completed).length;
      const pendingCount = finalForms.filter((form) => !form.completed).length;
      const pendingMandatoryCount = finalForms.filter(
        (form) =>
          String(form.requirement || "").toLowerCase() === "mandatory" &&
          !form.completed,
      ).length;

      const formNames = finalForms
        .map((form) => form.form_name || form.name || "Unnamed Form")
        .filter(Boolean);

      setFormStatus({
        loading: false,
        total: totalCount,
        pending: pendingCount,
        pendingMandatory: pendingMandatoryCount,
        completed: completedCount,
        names: formNames,
      });
    } catch (error) {
      console.error("Error fetching order form status:", error);
      setFormStatus({
        loading: false,
        total: 0,
        pending: 0,
        pendingMandatory: 0,
        completed: 0,
        names: [],
        message: "Could not load form status.",
      });
    }
  }, [orderId, getItemFormIds, isRelevantOrderForm]);

  useEffect(() => {
    if (isOpen) {
      fetchOrderFormStatus();
    }
  }, [isOpen, orderId, fetchOrderFormStatus]);

  // Function to get category icon
  const getCategoryIcon = (categoryName) => {
    switch (categoryName) {
      case "Grooming":
        return <ContentCutIcon sx={{ color: "#6366f1" }} />;
      case "Boarding":
      case "Kennel Boarding":
        return <HotelIcon sx={{ color: "#8b5cf6" }} />;
      case "Day Care":
        return <ChildCareIcon sx={{ color: "#06b6d4" }} />;
      case "Dog Walks":
        return <DirectionsWalkIcon sx={{ color: "#10b981" }} />;
      case "Vet Clinic":
      case "Pet Vet Clinic":
        return <LocalHospitalIcon sx={{ color: "#ef4444" }} />;
      // case "Pet Store":
      //   return <StorefrontIcon sx={{ color: "#f59e0b" }} />;
      default:
        return <PetsIcon sx={{ color: "#6b7280" }} />;
    }
  };

  // Function to find category by name - IMPROVED VERSION
  const findCategoryByName = (categoryName) => {
    
    for (const category of categories) {
      if (category.service_type) {
        for (const serviceType of category.service_type) {
          // Try exact match first
          if (serviceType.service_type_name === categoryName) {
            return {
              category,
              serviceType,
              payload: {
                parentName: category.service_category_name,
                childName: serviceType.service_type_name,
                serviceTypeId: serviceType.service_category_type_id,
              }
            };
          }
          // Try case-insensitive match
          if (serviceType.service_type_name.toLowerCase() === categoryName.toLowerCase()) {
            return {
              category,
              serviceType,
              payload: {
                parentName: category.service_category_name,
                childName: serviceType.service_type_name,
                serviceTypeId: serviceType.service_category_type_id,
              }
            };
          }
          // Try partial match (for "Grooming" vs "Pet Grooming")
          if (serviceType.service_type_name.toLowerCase().includes(categoryName.toLowerCase())) {
            return {
              category,
              serviceType,
              payload: {
                parentName: category.service_category_name,
                childName: serviceType.service_type_name,
                serviceTypeId: serviceType.service_category_type_id,
              }
            };
          }
        }
      }
    }
    
    console.log("❌ No category found with name:", categoryName);
    return null;
  };

  // Function to auto-select first available category as fallback
  const autoSelectFirstCategory = () => {
    if (categories.length > 0 && categories[0].service_type && categories[0].service_type.length > 0) {
      const firstCategory = categories[0];
      const firstServiceType = firstCategory.service_type[0];
      console.log("🔄 Auto-selecting first available category:", firstServiceType.service_type_name);
      
      return {
        category: firstCategory,
        serviceType: firstServiceType,
        payload: {
          parentName: firstCategory.service_category_name,
          childName: firstServiceType.service_type_name,
          serviceTypeId: firstServiceType.service_category_type_id,
        }
      };
    }
    return null;
  };

  // Function to refresh appointment data from API
  const refreshAppointmentData = async () => {
    if (itemData?.ref_id && onRefreshData) {
      try {
        const response = await httpService.get(
          `/vendorappointment/getVendorAppointment/${itemData.ref_id}`
        );
        if (response?.data) {
          onRefreshData(response.data);
        }
      } catch (error) {
        console.error("Error refreshing appointment data:", error);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset state when drawer is closed so it opens fresh next time
      setSelectedCategory("");
      setSelectedCategoryData(null);
      setIsInPetForm(false);

      // Refresh boarding calendar and queue data when drawer closes (for boarding operations)
      if (
        fromCalendar &&
        (window.refreshBoardingData || window.refreshBookingData)
      ) {
        if (window.refreshBoardingData) {
          window.refreshBoardingData();
        } else {
          window.refreshBookingData();
        }
      }
    } else if (isOpen && itemData && Object.keys(itemData).length > 0) {
      // When opening with itemData (edit mode), reset category state to allow re-matching
      setSelectedCategory("");
      setSelectedCategoryData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // NEW: Separate useEffect specifically for auto-selecting category
  useEffect(() => {
    if (isOpen && categories.length > 0 && !selectedCategory) {
      let categoryToSelect = null;

      if (itemData && Object.keys(itemData).length > 0 && !itemData.fromDrag) {
        return;
      }

      // ✅ Auto-select based on saleType
      if (saleType === "grooming") {
        categoryToSelect = findCategoryByName("Grooming Salon") 
          || findCategoryByName("Grooming") 
          || autoSelectFirstCategory();
      } else if (saleType === "vet") {
        categoryToSelect = findCategoryByName("Pet Vet Clinic")  // ✅ exact service_type_name
          || findCategoryByName("Vet Clinic")                     // fallback to category name
          || findCategoryByName("vet")
          || autoSelectFirstCategory();
      }else if (saleType === "booking") {
        categoryToSelect = autoSelectFirstCategory();
      } else {
        // Default: try Grooming first, then first available
        categoryToSelect = findCategoryByName("Grooming") || autoSelectFirstCategory();
      }

      if (categoryToSelect) {
        setSelectedCategory(categoryToSelect.serviceType.service_category_type_id);
        setSelectedCategoryData(categoryToSelect.payload);
      }
    }
  }, [isOpen, categories, selectedCategory, saleType]);

  // Keep your existing useEffect for edit mode logic, but add a fallback
  useEffect(() => {
    // Don't auto-select category if data comes from calendar drag
    if (itemData?.fromDrag) {
      return;
    }

    if (itemData && categories.length > 0 && !selectedCategory) {
      let match = null;

      // 🚨 CRITICAL FIX: Remove the restrictive filter that's causing the issue
      const allowServiceTypeByFilter = (serviceTypeName, serviceTypeId) => {
        // 🚨 ALLOW ALL SERVICE TYPES - Remove any filtering that blocks proper matching
        // The priority-based matching below will handle it correctly
        return true;
      };

      // Search through the TokenContext categories structure
      for (const category of categories) {
        if (category.service_type) {
          for (const serviceType of category.service_type) {
            // 🚨 FIXED: Use the updated filter function
            if (
              !allowServiceTypeByFilter(
                serviceType.service_type_name,
                serviceType.service_category_type_id
              )
            ) {
              continue;
            }

            // 0️⃣ 🚨 HIGHEST PRIORITY: Exact service_category_type_id match
            if (
              itemData.service_category_type_id &&
              serviceType.service_category_type_id ===
                itemData.service_category_type_id
            ) {
              match = { category, serviceType };
              break;
            }
          }
          if (match) break;
        }
        if (match) break;
      }

      // 1️⃣ SECOND PRIORITY: service_type_id alias match
      if (!match && itemData.service_type_id) {
        for (const category of categories) {
          if (category.service_type) {
            for (const serviceType of category.service_type) {
              if (
                serviceType.service_category_type_id ===
                itemData.service_type_id
              ) {
                match = { category, serviceType };
                break;
              }
            }
            if (match) break;
          }
          if (match) break;
        }
      }

      // 2️⃣ THIRD PRIORITY: item_type match (very reliable)
      if (!match && itemData.item_type) {
        const itemType = itemData.item_type.toLowerCase();
        for (const category of categories) {
          if (category.service_type) {
            for (const serviceType of category.service_type) {
              const serviceTypeName =
                serviceType.service_type_name?.toLowerCase() || "";

              if (
                (itemType === "grooming" &&
                  serviceTypeName.includes("grooming")) ||
                (itemType === "boarding" &&
                  serviceTypeName.includes("boarding")) ||
                (itemType === "day_care" &&
                  (serviceTypeName.includes("day_care") ||
                    serviceTypeName.includes("day care"))) ||
                (itemType === "dog_walking" &&
                  (serviceTypeName.includes("dog") ||
                    serviceTypeName.includes("walk"))) ||
                (itemType === "vet_clinic" &&
                  (serviceTypeName.includes("vet") ||
                    serviceTypeName.includes("clinic")))
              ) {
                match = { category, serviceType };
                break;
              }
            }
            if (match) break;
          }
          if (match) break;
        }
      }

      // 3️⃣ FOURTH PRIORITY: service_type_name exact match
      if (!match && itemData.service_type_name) {
        const targetName = itemData.service_type_name.toLowerCase();
        for (const category of categories) {
          if (category.service_type) {
            for (const serviceType of category.service_type) {
              const serviceTypeName =
                serviceType.service_type_name?.toLowerCase() || "";
              if (serviceTypeName === targetName) {
                match = { category, serviceType };
                break;
              }
            }
            if (match) break;
          }
          if (match) break;
        }
      }

      // 4️⃣ FIFTH PRIORITY: service_id match
      if (!match && itemData.service_id) {
        for (const category of categories) {
          if (category.service_type) {
            for (const serviceType of category.service_type) {
              if (serviceType.service_type_id === itemData.service_id) {
                match = { category, serviceType };
                break;
              }
            }
            if (match) break;
          }
          if (match) break;
        }
      }

      // 🚨 REMOVED: All fuzzy matching logic that was causing incorrect matches
      // This includes the service_name pattern matching that was causing the issue

      if (match) {
        const payload = {
          parentName: match.category.service_category_name,
          childName: match.serviceType.service_type_name,
          serviceTypeId: match.serviceType.service_category_type_id,
        };
        setSelectedCategory(match.serviceType.service_category_type_id);
        setSelectedCategoryData(payload);
      } else {
        // FALLBACK: If no specific match found in edit mode, still try to default to something
        console.log("🔍 Edit mode: No specific match found, using fallback");
        let fallbackCategory = findCategoryByName("Grooming") || autoSelectFirstCategory();
        if (fallbackCategory) {
          setSelectedCategory(fallbackCategory.serviceType.service_category_type_id);
          setSelectedCategoryData(fallbackCategory.payload);
        }
      }
    }
  }, [categories, itemData, selectedCategory, isOpen]);

  const handleCategorySelect = (value) => {
    setSelectedCategory(value);

    if (!value) {
      setSelectedCategoryData(null);
      return;
    }

    // Find the selected service type from categories (now including both typeId 1 and 2)
    let selection = null;
    for (const category of categories) {
      if (category.service_type) {
        for (const serviceType of category.service_type) {
          if (serviceType.service_category_type_id === value) {
            selection = {
              parentName: category.service_category_name,
              childName: serviceType.service_type_name,
              serviceTypeId: serviceType.service_category_type_id,
            };
            break;
          }
        }
      }
      if (selection) break;
    }

    setSelectedCategoryData(selection);
  };

  const handleServiceFormClose = () => {
    setSelectedCategory("");
    setSelectedCategoryData(null);
    setIsInPetForm(false);
    onClose();
  };

  // Handle back button click
  const handleBackClick = () => {
    if (isInPetForm) {
      // If we're in pet form, communicate back to service drawer to close pet form
      if (serviceDrawerRef.current && serviceDrawerRef.current.closePetForm) {
        serviceDrawerRef.current.closePetForm();
      }
      setIsInPetForm(false);
    } else {
      onClose();
    }
  };

  // Callback to handle pet form state changes from NewServiceDrawer
  const handlePetFormStateChange = (isShowingPetForm) => {
    setIsInPetForm(isShowingPetForm);
  };

  // Create a routing function that directs data to the appropriate handler
  const handleServiceUpdate = (data) => {
    onUpdate(data);
  };
  // const enabledPetSizeMap = useMemo(() => {
  //   const map = {};

  //   petTypes.forEach((pet) => {
  //     if (!pet.enabled) return;

  //     const enabledSizes = pet.sizes
  //       ?.filter((s) => s.enabled)
  //       .map((s) => s.size_label.toLowerCase());

  //     map[pet.name.toLowerCase()] = enabledSizes;
  //   });

  //   return map;
  // }, [petTypes]);

  const renderServiceForm = () => {
    if (!selectedCategoryData)
      return (
        <Fade in timeout={400}>
          <Box
            sx={{
              flex: 1,
              height: "auto",
              p: 4,
              bgcolor: "background.default",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease-in-out",
            }}
          >
            <CategoryIcon
              color="primary"
              sx={{
                fontSize: 72,
                mb: 2,
                opacity: 0.85,
                transform: "scale(1)",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
            />
            <Typography variant="h6" color="text.primary" gutterBottom>
              No Service Category Selected
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 320, mb: 3 }}
            >
              Please choose a service category to continue. You can pick one
              from the list above.
            </Typography>
          </Box>
        </Fade>
      );

    const categoryName =
      selectedCategoryData.parentName || selectedCategoryData.childName;

    // If opened via history reorder, force add mode by disabling edit state
    // const effectiveEditMode = editMode && !itemData?._fromHistoryReorder;
    const fromHistoryReorder = !!itemData?._fromHistoryReorder;

    // Render the appropriate service form based on category
    // Use clientId from itemData if available (edit mode), otherwise use passed clientId
    const effectiveClientId = itemData?.vendor_client_id || clientId;

    const childName = (selectedCategoryData.childName || "").toLowerCase();
    const parentName = (selectedCategoryData.parentName || "").toLowerCase();

    const isVet = categoryName === "Vet Clinic" || childName.includes("vet") || childName.includes("clinic") || parentName.includes("vet") || saleType === "vet";
    const isBoarding = !isVet && (categoryName === "Boarding" || childName.includes("board") || parentName.includes("board"));
    const isDaycare = !isVet && !isBoarding && (categoryName === "Day Care" || childName.includes("day care") || childName.includes("day_care"));
    const isDogWalks = !isVet && !isBoarding && !isDaycare && (categoryName === "Dog Walks" || childName.includes("dog") || childName.includes("walk"));

    if (isVet) {
      return (
        <VetClinicDrawer
          ref={serviceDrawerRef}
          isOpen={true}
          onClose={handleServiceFormClose}
          onUpdate={handleServiceUpdate}
          clientId={effectiveClientId}
          itemData={itemData}
          orderId={orderId}
          fromCalendar={fromCalendar}
          selectedSlotInfo={selectedSlotInfo}
          renderAsContent={true}
          editMode={editMode || isServiceEditMode}
          onRefreshData={refreshAppointmentData}
          appointmentStatusRef={appointmentStatusRef}
          serviceTypeId={selectedCategoryData.serviceTypeId}
          onPetFormStateChange={handlePetFormStateChange}
          autoOpenPetForm={autoOpenPetForm}
          fromHistoryReorder={fromHistoryReorder}
          isPetDetails={isPetDetails}
          existingAppointments={existingAppointments}
          checkEmployeeDoubleBooking={checkEmployeeDoubleBooking}
          categoryTypeName={selectedCategoryData.childName}
          setSaveEnquiryClicked={setSaveEnquiryClicked}
        />
      );
    }

    if (isBoarding) {
      return (
        <BoardingDrawer
          ref={serviceDrawerRef}
          isOpen={true}
          onClose={handleServiceFormClose}
          onUpdate={handleServiceUpdate}
          clientId={effectiveClientId}
          itemData={{
            ...itemData,
            service_type_name: itemData?.service_type_name || "Boarding",
            service_type_id: itemData?.service_type_id || selectedCategoryData.serviceTypeId,
            // start_date: selectedSlotInfo?.startDate || itemData?.start_date,
            // end_date: selectedSlotInfo?.endDate || itemData?.end_date,
            // checkin_date: selectedSlotInfo?.startDate || itemData?.checkin_date,
            // checkout_date: selectedSlotInfo?.endDate || itemData?.checkout_date,
            start_date: itemData?.start_date || selectedSlotInfo?.startDate,
            end_date: itemData?.end_date || selectedSlotInfo?.endDate,
            checkin_date: itemData?.checkin_date || itemData?.start_date || selectedSlotInfo?.startDate,
            checkout_date: itemData?.checkout_date || itemData?.end_date || selectedSlotInfo?.endDate,
          }}
          orderId={orderId}
          fromCalendar={fromCalendar}
          selectedSlotInfo={selectedSlotInfo}
          renderAsContent={true}
          editMode={editMode || isServiceEditMode}
          onRefreshData={refreshAppointmentData}
          appointmentStatusRef={appointmentStatusRef}
          serviceTypeId={selectedCategoryData.serviceTypeId}
          onPetFormStateChange={handlePetFormStateChange}
          autoOpenPetForm={autoOpenPetForm}
          fromHistoryReorder={fromHistoryReorder}
          isPetDetails={isPetDetails}
          categoryTypeName={selectedCategoryData.childName}
          setSaveEnquiryClicked={setSaveEnquiryClicked}
        />
      );
    }

    if (isDaycare) {
      return (
        <DaycareDrawer
          ref={serviceDrawerRef}
          isOpen={true}
          onClose={handleServiceFormClose}
          onUpdate={handleServiceUpdate}
          clientId={effectiveClientId}
          itemData={itemData}
          orderId={orderId}
          fromCalendar={fromCalendar}
          selectedSlotInfo={selectedSlotInfo}
          renderAsContent={true}
          editMode={editMode || isServiceEditMode}
          onRefreshData={refreshAppointmentData}
          appointmentStatusRef={appointmentStatusRef}
          serviceTypeId={selectedCategoryData.serviceTypeId}
          onPetFormStateChange={handlePetFormStateChange}
          autoOpenPetForm={autoOpenPetForm}
          fromHistoryReorder={fromHistoryReorder}
          suppressToasts={true}
          isPetDetails={isPetDetails}
          categoryTypeName={selectedCategoryData.childName}
        />
      );
    }

    if (isDogWalks) {
      return (
        <DogWalkingDrawer
          ref={serviceDrawerRef}
          isOpen={true}
          onClose={handleServiceFormClose}
          onUpdate={handleServiceUpdate}
          clientId={effectiveClientId}
          itemData={itemData}
          orderId={orderId}
          fromCalendar={fromCalendar}
          selectedSlotInfo={selectedSlotInfo}
          renderAsContent={true}
          editMode={editMode || isServiceEditMode}
          onRefreshData={refreshAppointmentData}
          appointmentStatusRef={appointmentStatusRef}
          serviceTypeId={selectedCategoryData.serviceTypeId}
          onPetFormStateChange={handlePetFormStateChange}
          autoOpenPetForm={autoOpenPetForm}
          fromHistoryReorder={fromHistoryReorder}
          isPetDetails={isPetDetails}
          categoryTypeName={selectedCategoryData.childName}
        />
      );
    }

    // Default: Grooming
    return (
      <NewServiceDrawer
        ref={serviceDrawerRef}
        isOpen={true}
        onClose={handleServiceFormClose}
        onUpdate={handleServiceUpdate}
        clientId={effectiveClientId}
        itemData={itemData}
        orderId={orderId}
        fromCalendar={fromCalendar}
        selectedSlotInfo={selectedSlotInfo}
        renderAsContent={true}
        editMode={editMode || isServiceEditMode}
        onRefreshData={refreshAppointmentData}
        appointmentStatusRef={appointmentStatusRef}
        serviceTypeId={selectedCategoryData.serviceTypeId}
        onPetFormStateChange={handlePetFormStateChange}
        autoOpenPetForm={autoOpenPetForm}
        fromHistoryReorder={fromHistoryReorder}
        isPetDetails={isPetDetails}
        existingAppointments={existingAppointments}
        checkEmployeeDoubleBooking={checkEmployeeDoubleBooking}
        categoryTypeName={selectedCategoryData.childName}
        setSaveEnquiryClicked={setSaveEnquiryClicked}
      />
    );
  };

  // 🚫 Lock body scroll while drawer is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // useEffect(() => {
  //   const fetchPetTypes = async () => {
  //     try {
  //       const res = await httpService.get("/vendors/getPetTypes");
  //       console.log("🐶 API petTypes:", res.data.data); 
  //       console.log("🐶 API petTypes res.data:", res.data);
  //       if (res?.data) {
  //         setPetTypes(res.data);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching pet types:", err);
  //     }
  //   };

  //   fetchPetTypes();
  // }, []);

  

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full ${drawerWidth || "lg:w-1/2"} bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Enhanced Header with gradient */}
        <div className="flex justify-start items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm gap-3">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/60 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <HiArrowSmLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              {isInPetForm ? (
                <PetsIcon sx={{ color: "#6366f1", fontSize: 24 }} />
              ) : (
                <CategoryIcon sx={{ color: "#6366f1", fontSize: 24 }} />
              )}
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              {isInPetForm ? (
                "Add New Pet"
              ) : editMode ? (
                <div className="flex flex-col">
                  <div>
                    Edit Pet & Service
                    <span className="text-sm ml-1">
                      {clientName
                        ? `(${clientName}${
                            clientNumber ? ` - ${clientNumber}` : ""
                          })`
                        : ""}
                    </span>
                  </div>
                  <><span className="text-sm">{selectedCategoryData?.childName}</span></>
                </div>
              ) : (
                <>
                  Add Pet & Service
                  <span className="text-sm ml-1">
                    {clientName
                      ? `(${clientName}${
                          clientNumber ? ` - ${clientNumber}` : ""
                        })`
                      : ""}
                  </span>
                </>
              )}
            </h2>
          </div>
          {/* {(!isInPetForm && (formStatus.loading || formStatus.total > 0)) && (
            <div className="mt-3 px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  formStatus.loading
                    ? "bg-blue-100 text-blue-700"
                    : formStatus.pendingMandatory > 0
                    ? "bg-red-100 text-red-700"
                    : formStatus.pending > 0
                    ? "bg-orange-100 text-orange-700"
                    : "bg-green-100 text-green-700"
                }`}>
                  <FiAlertCircle className="w-5 h-5" />
                </div>
                <div className="space-y-1 flex-1">
                  <p className="text-sm font-semibold text-gray-800">
                    {formStatus.loading
                      ? "Checking form status…"
                      : `Form: ${formStatus.names[0] || "Linked form"}`}
                  </p>
                  {!formStatus.loading && formStatus.names.length > 1 && (
                    <p className="text-xs text-gray-500">
                      +{formStatus.names.length - 1} more form{formStatus.names.length - 1 === 1 ? "" : "s"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Content */}
        <div className="flex flex-col" style={{ minHeight: "calc(100vh - 80px)" }}>
          <div className="p-1 space-y-1 overflow-auto bg-gradient-to-b from-gray-50/50 to-white pb-2">
            {/* Enhanced Category Dropdown - Hide when in pet form */}
            {!isInPetForm && !itemData?.ref_id && (
              <div className="space-y-2">
                <label className="block text-md font-semibold text-gray-700 m-2 flex items-center gap-2">
                  <CategoryIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                  Service Category
                </label>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={selectedCategory}
                  onChange={(e) => handleCategorySelect(e.target.value)}
                  disabled={loading}
                  placeholder="Choose a service category"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: selectedCategoryData ? 1 : 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: "white",
                      border: "2px solid transparent",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "#e5e7eb",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                      "&.Mui-focused": {
                        borderColor: "#6366f1",
                        boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#6b7280",
                      fontWeight: 500,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <div className="flex items-center gap-3 py-1">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <CategoryIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                      </div>
                      <span className="text-gray-500">
                        {loading
                          ? "Loading categories…"
                          : "Choose a service category"}
                      </span>
                    </div>
                  </MenuItem>
                  {categories.flatMap((cat) =>
                    cat.service_type
                      ? cat.service_type.map((serviceType) => (
                          <MenuItem
                            key={serviceType.service_category_type_id}
                            value={serviceType.service_category_type_id}
                            sx={{
                              py: 1.5,
                              "&:hover": {
                                backgroundColor: "#f8fafc",
                              },
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-center shadow-sm">
                                {getCategoryIcon(serviceType.service_type_name)}
                              </div>
                              <span className="font-medium text-gray-700">
                                {serviceType.service_type_name}
                              </span>
                            </div>
                          </MenuItem>
                        ))
                      : []
                  )}
                </TextField>
              </div>
            )}

            {/* Service Form Content - Always render below dropdown */}
            {renderServiceForm()}
          </div>

          {/* Enhanced Footer with gradient - Hide when category is selected or in pet form */}
          {!selectedCategoryData && !isInPetForm && (
            <div className="fixed bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 shadow-lg z-10">
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-2xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PetServiceCategoryDrawer;